import type { IProxy, IProxyListItem } from '@hit-game/shared/types';
import { Activity, Plus, Shield } from 'lucide-react';
import { useCallback, useState } from 'react';

import { DataStateMessage } from '../../components/common/DataStateMessage';
import { DataToolbar } from '../../components/common/DataToolbar';
import { NumberedPagination } from '../../components/common/NumberedPagination';
import { ProxyAddModal, ProxyEditModal } from '../../components/proxy/ProxyFormModal';
import { ProxyRow } from '../../components/proxy/ProxyRow';
import { useToast } from '../../context/ToastContext';
import { useMutation } from '../../hooks/useMutation';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { proxyService } from '../../services/proxy.service';

const COLUMNS = ['Trạng thái', 'Proxy', 'Tài khoản', 'Lần cuối', 'Action'];

export function ProxyPage() {
  const toast = useToast();

  const fetcher = useCallback(
    (params: { page: number; limit: number; search?: string }) => proxyService.list(params),
    [],
  );
  const list = usePaginatedList<IProxyListItem>(fetcher);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<IProxy | null>(null);
  const [checkingValue, setCheckingValue] = useState<string | null>(null);
  const [checkingAll, setCheckingAll] = useState(false);

  const mutate = useMutation(list.reload);

  const addMany = (values: string[]) => mutate(() => proxyService.createMany(values));
  const update = (id: string, value: string) => mutate(() => proxyService.update(id, value));
  const remove = (id: string) => mutate(() => proxyService.remove(id));

  /**
   * Check MỘT proxy.
   *
   * `unknown` = lỗi ở phía máy chủ đi check, KHÔNG phải proxy chết. Báo "không
   * phản hồi" ở đây là đổ oan cho proxy, mà trạng thái trong bảng cũng không đổi
   * nên người dùng sẽ tưởng nút hỏng.
   */
  const checkOne = async (value: string) => {
    setCheckingValue(value);
    try {
      const [result] = await proxyService.checkHealth([value]);

      if (result?.status === 'unknown') {
        toast.info('Chưa kết luận được — lỗi từ phía máy chủ, thử lại sau');
      } else if (result?.alive) {
        toast.success('Proxy hoạt động');
      } else {
        toast.error('Proxy không phản hồi');
      }
      list.reload();
    } catch {
      toast.error('Không kiểm tra được proxy');
    } finally {
      setCheckingValue(null);
    }
  };

  /**
   * Check các proxy ĐANG hiện trên trang — đúng những dòng người dùng nhìn thấy
   * đổi trạng thái ngay sau đó.
   *
   * Nhịp `unknown` không kết luận được nên trạng thái trong bảng GIỮ NGUYÊN. Báo
   * "thành công" khi có `unknown` là để người dùng ngồi nhìn mấy dòng không đổi
   * mà tưởng nút hỏng.
   */
  const checkAll = async () => {
    setCheckingAll(true);
    try {
      const results = await proxyService.checkHealth(list.items.map((proxy) => proxy.value));
      const unknown = results.filter((item) => item.status === 'unknown').length;

      if (unknown > 0) {
        toast.info(`${String(unknown)}/${String(results.length)} proxy chưa kết luận được`);
      } else {
        toast.success('Check proxy thành công');
      }
      list.reload();
    } catch {
      toast.error('Không kiểm tra được proxy');
    } finally {
      setCheckingAll(false);
    }
  };

  /**
   * Bảng rỗng vì LỌC HẾT hay vì CHƯA CÓ GÌ — hai tình huống, hai lời hướng dẫn
   * khác nhau. `undefined` = không rỗng, khỏi hiện gì.
   */
  const emptyState =
    list.loading || list.items.length > 0
      ? undefined
      : list.search
        ? { message: 'Không tìm thấy proxy phù hợp', hint: 'Thử tìm kiếm với từ khóa khác' }
        : { message: 'Chưa có proxy nào', hint: 'Thêm proxy ở ô bên trên để bắt đầu' };

  return (
    <div className="text-ink space-y-4">
      <div className="border-line bg-panel overflow-hidden rounded-xl border">
        <DataToolbar
          icon={<Shield size={16} />}
          title="Danh sách Proxies"
          count={list.total}
          searchValue={list.search}
          onSearchChange={list.setSearch}
          searchPlaceholder="Tìm kiếm proxy..."
          action={
            <>
              <button
                type="button"
                onClick={() => {
                  setAddOpen(true);
                }}
                className="bg-accent hover:bg-accent-hover ml-2 flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-bold text-white transition-colors"
              >
                <Plus size={13} />
                Thêm
              </button>
              <button
                type="button"
                onClick={() => {
                  void checkAll();
                }}
                disabled={checkingAll || list.total === 0}
                className="text-success border-success-border bg-success-deep hover:bg-success-border hover:text-success-bright ml-2 flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Activity size={14} className={checkingAll ? 'animate-spin' : ''} />
                {checkingAll ? 'Đang check...' : 'Health Check All'}
              </button>
            </>
          }
        />

        <DataStateMessage
          loading={list.loading}
          empty={emptyState?.message ?? null}
          emptyHint={emptyState?.hint}
          emptyIcon={<Shield size={32} className="text-line" />}
        />

        {!list.loading && list.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-canvas-raised">
                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      className="text-ink-faint px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em]"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.items.map((proxy) => (
                  <ProxyRow
                    key={proxy._id ?? proxy.value}
                    proxy={proxy}
                    checkingValue={checkingValue}
                    onCheckHealth={(value) => {
                      void checkOne(value);
                    }}
                    onEdit={setEditing}
                    onDelete={remove}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <NumberedPagination
          page={list.page}
          totalPages={list.totalPages}
          onPageChange={list.setPage}
          summary={
            <>
              Tổng: <strong className="text-ink">{list.total}</strong> proxy
            </>
          }
        />
      </div>

      <ProxyAddModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onSubmit={addMany}
      />
      <ProxyEditModal
        proxy={editing}
        onClose={() => {
          setEditing(null);
        }}
        onSave={update}
      />
    </div>
  );
}
